import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { toast } from 'sonner';

import {
  ArrowLeft,
  ArrowRight,
  ChatText,
  Check,
  CheckCircle,
  Code,
  Copy,
  Database,
  Eye,
  FileCode,
  Lightning,
  PaperPlaneRight,
  Play,
  Robot,
  Terminal,
  WarningCircle,
  WebhooksLogo
} from '@phosphor-icons/react';
const WebhookTutorialPage = () => {
  const [copiedSection, setCopiedSection] = useState(null);

  const copyToClipboard = (text, section) => {
    navigator.clipboard.writeText(text);
    setCopiedSection(section);
    toast.success('Copied to clipboard!');
    setTimeout(() => setCopiedSection(null), 2000);
  };

  const systemPrompt = `You are a data processing assistant. Your job is to:

1. ANALYZE the incoming webhook data
2. EXTRACT key information (who sent it, what they need, priority level)
3. CATEGORIZE the request (inquiry, complaint, order, feedback, other)
4. DRAFT an appropriate response

INPUT DATA:
{{input}}

Respond in this exact JSON format:
{
  "sender": "extracted sender name or email",
  "category": "inquiry|complaint|order|feedback|other",
  "priority": "low|medium|high",
  "summary": "one sentence summary of the request",
  "suggested_response": "your drafted response to send back",
  "extracted_data": {}
}

Always respond with valid JSON only. No additional text.`;

  const testCurl = `curl -X POST "https://api.manandmachine.app/api/agents/YOUR_AGENT_ID/run" \\
  -H "Content-Type: application/json" \\
  -H "Authorization: Bearer YOUR_API_KEY" \\
  -d '{
    "input": {
      "from": "john.doe@example.com",
      "subject": "Product Inquiry",
      "message": "Hi, I am interested in your enterprise plan. Can you tell me more about pricing and features?"
    }
  }'`;

  const testJson = `{
  "input": {
    "from": "john.doe@example.com",
    "subject": "Product Inquiry",
    "message": "Hi, I am interested in your enterprise plan. Can you tell me more about pricing and features?"
  }
}`;

  const expectedOutput = `{
  "sender": "john.doe@example.com",
  "category": "inquiry",
  "priority": "medium",
  "summary": "Customer inquiring about enterprise plan pricing and features",
  "suggested_response": "Thank you for your interest in our enterprise plan! I'd be happy to help...",
  "extracted_data": {
    "topic": "enterprise plan",
    "intent": "pricing inquiry"
  }
}`;

  return (
    <>
      <Helmet>
        <title>Build a WebhooksLogo Agent - Tutorial | Man & Machine</title>
        <meta name="description" content="Step-by-step tutorial to build your first webhook agent. Learn how to receive data, process it with AI, and send results to external systems." />
      </Helmet>

      <div className="min-h-screen bg-[#161B26]">
        {/* Header */}
        <div className="border-b border-[rgba(255,255,255,0.1)] bg-[#161B26]/80 backdrop-blur-xl sticky top-0 z-50">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 py-4">
            <Link 
              to="/ai-agents" 
              className="inline-flex items-center gap-2 text-[#A0AEC0] hover:text-white transition-colors text-sm"
            >
              <ArrowLeft size={16} />
              Back to Learn
            </Link>
          </div>
        </div>

        {/* Content */}
        <div className="max-w-4xl mx-auto px-4 sm:px-6 py-8 sm:py-12">
          {/* Title Section */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="mb-8 sm:mb-12"
          >
            <div className="flex items-center gap-3 mb-4">
              <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-green-500 to-emerald-600 flex items-center justify-center">
                <Lightning size={24} className="text-white" />
              </div>
              <div>
                <span className="text-xs text-[#6366F1] font-medium uppercase tracking-wider">Tutorial</span>
                <h1 className="text-2xl sm:text-3xl font-bold text-white">Build Your First WebhooksLogo Agent</h1>
              </div>
            </div>
            <p className="text-[#A0AEC0] text-base sm:text-lg max-w-2xl">
              Learn the fundamentals of Man & Machine by building a simple but powerful agent that receives webhook data, processes it with AI, and sends results to an external system.
            </p>
            <div className="flex items-center gap-4 mt-4 text-sm text-zinc-500">
              <span>15 min read</span>
              <span>•</span>
              <span>Beginner friendly</span>
            </div>
          </motion.div>

          {/* What We're Building */}
          <motion.section
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="mb-10"
          >
            <h2 className="text-xl font-bold text-white mb-4 flex items-center gap-2">
              <span className="w-8 h-8 rounded-lg bg-[#6366F1]/20 flex items-center justify-center text-[#6366F1] text-sm font-bold">1</span>
              What We're Building
            </h2>
            
            <div className="glass rounded-xl p-6 mb-4">
              <div className="flex flex-col sm:flex-row items-center justify-center gap-4 sm:gap-8 text-center">
                <div className="flex flex-col items-center">
                  <div className="w-16 h-16 rounded-xl bg-blue-500/20 flex items-center justify-center mb-2">
                    <WebhooksLogo size={28} className="text-blue-400" />
                  </div>
                  <span className="text-white font-medium text-sm">WebhooksLogo IN</span>
                  <span className="text-zinc-500 text-xs">Receives data</span>
                </div>
                <ArrowRight size={24} className="text-zinc-600 rotate-90 sm:rotate-0" />
                <div className="flex flex-col items-center">
                  <div className="w-16 h-16 rounded-xl bg-purple-500/20 flex items-center justify-center mb-2">
                    <Robot size={28} className="text-purple-400" />
                  </div>
                  <span className="text-white font-medium text-sm">AI Agent</span>
                  <span className="text-zinc-500 text-xs">Processes & transforms</span>
                </div>
                <ArrowRight size={24} className="text-zinc-600 rotate-90 sm:rotate-0" />
                <div className="flex flex-col items-center">
                  <div className="w-16 h-16 rounded-xl bg-green-500/20 flex items-center justify-center mb-2">
                    <PaperPlaneRight size={28} className="text-green-400" />
                  </div>
                  <span className="text-white font-medium text-sm">WebhooksLogo OUT</span>
                  <span className="text-zinc-500 text-xs">Sends result</span>
                </div>
              </div>
            </div>

            <div className="glass rounded-xl p-4 border-l-4 border-[#6366F1]">
              <p className="text-[#A0AEC0] text-sm">
                <strong className="text-white">Use Case Example:</strong> Receive a customer inquiry via webhook, have AI categorize it and draft a response, then send the structured result to your CRM, Slack, or any other system.
              </p>
            </div>
          </motion.section>

          {/* Step 1: Create Agent */}
          <motion.section
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.15 }}
            className="mb-10"
          >
            <h2 className="text-xl font-bold text-white mb-4 flex items-center gap-2">
              <span className="w-8 h-8 rounded-lg bg-[#6366F1]/20 flex items-center justify-center text-[#6366F1] text-sm font-bold">2</span>
              Create a New Agent
            </h2>
            
            <div className="space-y-3">
              <div className="glass rounded-xl p-4 flex items-start gap-3">
                <CheckCircle size={20} className="text-green-400 flex-shrink-0 mt-0.5" />
                <div>
                  <p className="text-white text-sm font-medium">Navigate to AI Workforce → Agents</p>
                  <p className="text-zinc-500 text-xs">Find this in the left sidebar</p>
                </div>
              </div>
              <div className="glass rounded-xl p-4 flex items-start gap-3">
                <CheckCircle size={20} className="text-green-400 flex-shrink-0 mt-0.5" />
                <div>
                  <p className="text-white text-sm font-medium">Click "+ Create Agent" button</p>
                </div>
              </div>
              <div className="glass rounded-xl p-4 flex items-start gap-3">
                <CheckCircle size={20} className="text-green-400 flex-shrink-0 mt-0.5" />
                <div>
                  <p className="text-white text-sm font-medium">Select "Start from Scratch" or "Blank Agent"</p>
                </div>
              </div>
              <div className="glass rounded-xl p-4 flex items-start gap-3">
                <CheckCircle size={20} className="text-green-400 flex-shrink-0 mt-0.5" />
                <div>
                  <p className="text-white text-sm font-medium">Name your agent: <code className="px-2 py-0.5 rounded bg-zinc-800 text-[#6366F1]">WebhooksLogo Demo Agent</code></p>
                </div>
              </div>
              <div className="glass rounded-xl p-4 flex items-start gap-3">
                <CheckCircle size={20} className="text-green-400 flex-shrink-0 mt-0.5" />
                <div>
                  <p className="text-white text-sm font-medium">Click Create to open the Visual Flow Builder</p>
                </div>
              </div>
            </div>
          </motion.section>

          {/* Step 2: WebhooksLogo Input */}
          <motion.section
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="mb-10"
          >
            <h2 className="text-xl font-bold text-white mb-4 flex items-center gap-2">
              <span className="w-8 h-8 rounded-lg bg-[#6366F1]/20 flex items-center justify-center text-[#6366F1] text-sm font-bold">3</span>
              Configure the Input
            </h2>
            
            <div className="glass rounded-xl p-6 mb-4">
              <h3 className="text-white font-medium mb-3 flex items-center gap-2">
                <WebhooksLogo size={18} className="text-blue-400" />
                Add Input Node
              </h3>
              <ol className="space-y-2 text-[#A0AEC0] text-sm">
                <li className="flex items-start gap-2">
                  <span className="text-[#6366F1] font-medium">1.</span>
                  In the left panel, find the "Inputs" section
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-[#6366F1] font-medium">2.</span>
                  Drag a "Webhook" or "Text" input node onto the canvas
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-[#6366F1] font-medium">3.</span>
                  Click the node to configure it (optional: set validation rules)
                </li>
              </ol>
            </div>

            <div className="glass rounded-xl p-4 border-l-4 border-blue-500 mb-4">
              <div className="flex items-start gap-2">
                <WarningCircle size={18} className="text-blue-400 flex-shrink-0 mt-0.5" />
                <div className="text-[#A0AEC0] text-sm">
                  <strong className="text-blue-300">How to trigger your agent (after saving):</strong>
                  <p className="mt-1">Once you've completed building and saved your agent, you can trigger it via the <strong className="text-white">Run Agent API</strong>:</p>
                  <code className="block mt-2 px-3 py-2 bg-zinc-800 rounded text-xs text-green-400 font-mono">
                    POST /api/agents/{'{agent_id}'}/run
                  </code>
                  <p className="mt-2 text-xs"><strong className="text-white">Where to find your Agent ID:</strong></p>
                  <ul className="mt-1 text-xs space-y-1 ml-4">
                    <li>• <strong>In the URL</strong> - After saving, the URL will show: <code className="px-1 bg-zinc-800 rounded">/agents/<span className="text-green-400">abc123</span>/edit</code></li>
                    <li>• <strong>Agent Details page</strong> - Go to AI Workforce → Agents → Click your agent</li>
                    <li>• <strong>Run button</strong> - Click "Run" on your agent to see example API calls</li>
                  </ul>
                  <p className="mt-2 text-xs text-amber-300">💡 Continue building your agent first - you'll get the ID after saving in Step 6!</p>
                </div>
              </div>
            </div>
          </motion.section>

          {/* Step 3: AI Node */}
          <motion.section
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.25 }}
            className="mb-10"
          >
            <h2 className="text-xl font-bold text-white mb-4 flex items-center gap-2">
              <span className="w-8 h-8 rounded-lg bg-[#6366F1]/20 flex items-center justify-center text-[#6366F1] text-sm font-bold">4</span>
              Add the AI Processing Node
            </h2>
            
            <div className="glass rounded-xl p-6 mb-4">
              <h3 className="text-white font-medium mb-3 flex items-center gap-2">
                <Robot size={18} className="text-purple-400" />
                Configure the AI Node
              </h3>
              <ol className="space-y-2 text-[#A0AEC0] text-sm mb-4">
                <li className="flex items-start gap-2">
                  <span className="text-[#6366F1] font-medium">1.</span>
                  From the left panel under "AI", drag an AI node (e.g., "AI Data Extraction") onto the canvas
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-[#6366F1] font-medium">2.</span>
                  Connect your input node's output to this AI node
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-[#6366F1] font-medium">3.</span>
                  Click the node to open the configuration panel on the right
                </li>
              </ol>
              
              <div className="mt-4 p-4 bg-zinc-800/50 rounded-lg">
                <h4 className="text-white text-sm font-medium mb-3">Setup Tab Configuration:</h4>
                <ul className="space-y-2 text-[#A0AEC0] text-sm">
                  <li className="flex items-start gap-2">
                    <span className="text-purple-400">•</span>
                    <div>
                      <strong className="text-white">Node Name:</strong> Give it a descriptive name (e.g., "Categorize Request")
                    </div>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-purple-400">•</span>
                    <div>
                      <strong className="text-white">Capability:</strong> Select what the AI should do (e.g., "summarization", "extraction", "classification")
                    </div>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-purple-400">•</span>
                    <div>
                      <strong className="text-white">Text to Process:</strong> Use <code className="px-1.5 py-0.5 rounded bg-zinc-700 text-green-400">{"{{input}}"}</code> to pass data from the previous node
                    </div>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-purple-400">•</span>
                    <div>
                      <strong className="text-white">Fields to Extract:</strong> Define what data you want the AI to extract (see example below)
                    </div>
                  </li>
                </ul>
              </div>
            </div>

            <div className="glass rounded-xl overflow-hidden mb-4">
              <div className="flex items-center justify-between px-4 py-2 bg-zinc-800/50 border-b border-zinc-700">
                <span className="text-sm text-zinc-400 flex items-center gap-2">
                  <Code size={14} />
                  Example Fields to Extract
                </span>
                <button
                  onClick={() => copyToClipboard(systemPrompt, 'prompt')}
                  className="flex items-center gap-1 px-2 py-1 rounded text-xs text-zinc-400 hover:text-white hover:bg-zinc-700 transition-colors"
                >
                  {copiedSection === 'prompt' ? <Check size={14} className="text-green-400" /> : <Copy size={14} />}
                  {copiedSection === 'prompt' ? 'Copied!' : 'Copy'}
                </button>
              </div>
              <pre className="p-4 text-sm text-[#A0AEC0] overflow-x-auto">
                <code>{`sender: The email or name of the person who sent this
category: One of: inquiry, complaint, order, feedback, other
priority: One of: low, medium, high
summary: A one-sentence summary of the request
suggested_response: A draft response to send back`}</code>
              </pre>
            </div>

            <div className="glass rounded-xl p-4 border-l-4 border-purple-500">
              <div className="flex items-start gap-2">
                <WarningCircle size={18} className="text-purple-400 flex-shrink-0 mt-0.5" />
                <div className="text-[#A0AEC0] text-sm">
                  <strong className="text-purple-300">Tip - System Prompt Location:</strong>
                  <p className="mt-1">For custom instructions, you can also add a system prompt in the <strong className="text-white">Config tab</strong> on the left panel under "System Prompt". This applies to the entire agent.</p>
                </div>
              </div>
            </div>
          </motion.section>

          {/* Step 4: Save First */}
          <motion.section
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
            className="mb-10"
          >
            <h2 className="text-xl font-bold text-white mb-4 flex items-center gap-2">
              <span className="w-8 h-8 rounded-lg bg-[#6366F1]/20 flex items-center justify-center text-[#6366F1] text-sm font-bold">5</span>
              Save Your Agent
            </h2>
            
            <div className="glass rounded-xl p-6 mb-4">
              <div className="space-y-3">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-lg bg-blue-500/20 flex items-center justify-center">
                    <Database size={20} className="text-blue-400" />
                  </div>
                  <div>
                    <p className="text-white font-medium">Click "Save" in the top right corner</p>
                    <p className="text-zinc-500 text-xs">This saves your flow and creates the agent</p>
                  </div>
                </div>
              </div>
            </div>

            <div className="glass rounded-xl p-4 border-l-4 border-amber-500">
              <div className="flex items-start gap-2">
                <WarningCircle size={18} className="text-amber-400 flex-shrink-0 mt-0.5" />
                <p className="text-[#A0AEC0] text-sm">
                  <strong className="text-amber-300">Important:</strong> You must save the agent before you can add Automations (like WebhooksLogo POST). After saving, your Agent ID will appear in the URL.
                </p>
              </div>
            </div>
          </motion.section>

          {/* Step 5: Add WebhooksLogo Output */}
          <motion.section
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.35 }}
            className="mb-10"
          >
            <h2 className="text-xl font-bold text-white mb-4 flex items-center gap-2">
              <span className="w-8 h-8 rounded-lg bg-[#6366F1]/20 flex items-center justify-center text-[#6366F1] text-sm font-bold">6</span>
              Add the WebhooksLogo POST Automation
            </h2>
            
            <div className="glass rounded-xl p-6 mb-4">
              <h3 className="text-white font-medium mb-3 flex items-center gap-2">
                <PaperPlaneRight size={18} className="text-green-400" />
                Configure WebhooksLogo Output (After Saving)
              </h3>
              <ol className="space-y-2 text-[#A0AEC0] text-sm">
                <li className="flex items-start gap-2">
                  <span className="text-[#6366F1] font-medium">1.</span>
                  Go to the <strong className="text-white">Config tab</strong> in the left panel
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-[#6366F1] font-medium">2.</span>
                  Scroll down to <strong className="text-white">Automations</strong> section
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-[#6366F1] font-medium">3.</span>
                  Click <strong className="text-white">+ Add Automation</strong>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-[#6366F1] font-medium">4.</span>
                  Select <strong className="text-white">"Webhook POST"</strong> from the options
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-[#6366F1] font-medium">5.</span>
                  Enter your destination URL (for testing, use <a href="https://webhook.site" target="_blank" rel="noopener noreferrer" className="text-[#6366F1] hover:underline">webhook.site</a>)
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-[#6366F1] font-medium">6.</span>
                  Click <strong className="text-white">Save</strong> again to save the automation
                </li>
              </ol>
            </div>

            <div className="glass rounded-xl p-4 border-l-4 border-green-500">
              <p className="text-[#A0AEC0] text-sm">
                <strong className="text-green-300">Testing Tip:</strong> Go to <a href="https://webhook.site" target="_blank" rel="noopener noreferrer" className="text-[#6366F1] hover:underline">webhook.site</a>, copy your unique URL, and paste it as the destination. You'll be able to see exactly what your agent sends after each run.
              </p>
            </div>
          </motion.section>

          {/* Step 6: Deploy */}
          <motion.section
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.4 }}
            className="mb-10"
          >
            <h2 className="text-xl font-bold text-white mb-4 flex items-center gap-2">
              <span className="w-8 h-8 rounded-lg bg-[#6366F1]/20 flex items-center justify-center text-[#6366F1] text-sm font-bold">7</span>
              Deploy Your Agent
            </h2>
            
            <div className="glass rounded-xl p-6">
              <div className="space-y-3">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-lg bg-green-500/20 flex items-center justify-center">
                    <Play size={20} className="text-green-400" />
                  </div>
                  <div>
                    <p className="text-white font-medium">Click "Deploy" or "Activate"</p>
                    <p className="text-zinc-500 text-xs">Makes your agent live and ready to receive requests</p>
                  </div>
                </div>
              </div>
            </div>
          </motion.section>

          {/* Step 8: Test */}
          <motion.section
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.4 }}
            className="mb-10"
          >
            <h2 className="text-xl font-bold text-white mb-4 flex items-center gap-2">
              <span className="w-8 h-8 rounded-lg bg-[#6366F1]/20 flex items-center justify-center text-[#6366F1] text-sm font-bold">8</span>
              Test Your Agent
            </h2>
            
            <div className="space-y-4">
              {/* cURL Test */}
              <div className="glass rounded-xl overflow-hidden">
                <div className="flex items-center justify-between px-4 py-2 bg-zinc-800/50 border-b border-zinc-700">
                  <span className="text-sm text-zinc-400 flex items-center gap-2">
                    <Terminal size={14} />
                    Test with cURL (Terminal)
                  </span>
                  <button
                    onClick={() => copyToClipboard(testCurl, 'curl')}
                    className="flex items-center gap-1 px-2 py-1 rounded text-xs text-zinc-400 hover:text-white hover:bg-zinc-700 transition-colors"
                  >
                    {copiedSection === 'curl' ? <Check size={14} className="text-green-400" /> : <Copy size={14} />}
                    {copiedSection === 'curl' ? 'Copied!' : 'Copy'}
                  </button>
                </div>
                <pre className="p-4 text-sm text-[#A0AEC0] overflow-x-auto">
                  <code>{testCurl}</code>
                </pre>
              </div>

              {/* JSON Test Data */}
              <div className="glass rounded-xl overflow-hidden">
                <div className="flex items-center justify-between px-4 py-2 bg-zinc-800/50 border-b border-zinc-700">
                  <span className="text-sm text-zinc-400 flex items-center gap-2">
                    <FileCode size={14} />
                    Test JSON Data (for Postman or built-in tester)
                  </span>
                  <button
                    onClick={() => copyToClipboard(testJson, 'json')}
                    className="flex items-center gap-1 px-2 py-1 rounded text-xs text-zinc-400 hover:text-white hover:bg-zinc-700 transition-colors"
                  >
                    {copiedSection === 'json' ? <Check size={14} className="text-green-400" /> : <Copy size={14} />}
                    {copiedSection === 'json' ? 'Copied!' : 'Copy'}
                  </button>
                </div>
                <pre className="p-4 text-sm text-[#A0AEC0] overflow-x-auto">
                  <code>{testJson}</code>
                </pre>
              </div>
            </div>
          </motion.section>

          {/* Expected Output */}
          <motion.section
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.45 }}
            className="mb-10"
          >
            <h2 className="text-xl font-bold text-white mb-4 flex items-center gap-2">
              <span className="w-8 h-8 rounded-lg bg-[#6366F1]/20 flex items-center justify-center text-[#6366F1] text-sm font-bold">8</span>
              Expected Output
            </h2>
            
            <p className="text-[#A0AEC0] text-sm mb-4">
              After the AI processes your webhook data, it will output structured JSON like this:
            </p>

            <div className="glass rounded-xl overflow-hidden">
              <div className="flex items-center justify-between px-4 py-2 bg-zinc-800/50 border-b border-zinc-700">
                <span className="text-sm text-zinc-400 flex items-center gap-2">
                  <Eye size={14} />
                  AI Output (sent to your output webhook)
                </span>
              </div>
              <pre className="p-4 text-sm text-green-400 overflow-x-auto">
                <code>{expectedOutput}</code>
              </pre>
            </div>
          </motion.section>

          {/* Understanding Data Flow */}
          <motion.section
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.5 }}
            className="mb-10"
          >
            <h2 className="text-xl font-bold text-white mb-4">Understanding Data Flow</h2>
            
            <div className="glass rounded-xl p-6">
              <h3 className="text-white font-medium mb-4">How Variables Work</h3>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-zinc-700">
                      <th className="text-left py-2 px-3 text-zinc-400 font-medium">Stage</th>
                      <th className="text-left py-2 px-3 text-zinc-400 font-medium">Variable</th>
                      <th className="text-left py-2 px-3 text-zinc-400 font-medium">Contains</th>
                    </tr>
                  </thead>
                  <tbody className="text-[#A0AEC0]">
                    <tr className="border-b border-zinc-800">
                      <td className="py-2 px-3">WebhooksLogo In</td>
                      <td className="py-2 px-3"><code className="px-1.5 py-0.5 rounded bg-zinc-800 text-blue-400">{"{{webhook.body}}"}</code></td>
                      <td className="py-2 px-3">Raw incoming JSON</td>
                    </tr>
                    <tr className="border-b border-zinc-800">
                      <td className="py-2 px-3">WebhooksLogo In</td>
                      <td className="py-2 px-3"><code className="px-1.5 py-0.5 rounded bg-zinc-800 text-blue-400">{"{{webhook.headers}}"}</code></td>
                      <td className="py-2 px-3">Request headers</td>
                    </tr>
                    <tr className="border-b border-zinc-800">
                      <td className="py-2 px-3">AI Node</td>
                      <td className="py-2 px-3"><code className="px-1.5 py-0.5 rounded bg-zinc-800 text-purple-400">{"{{input}}"}</code></td>
                      <td className="py-2 px-3">Data from previous node</td>
                    </tr>
                    <tr>
                      <td className="py-2 px-3">WebhooksLogo Out</td>
                      <td className="py-2 px-3"><code className="px-1.5 py-0.5 rounded bg-zinc-800 text-green-400">{"{{previous_output}}"}</code></td>
                      <td className="py-2 px-3">Result from AI node</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          </motion.section>

          {/* Troubleshooting */}
          <motion.section
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.55 }}
            className="mb-10"
          >
            <h2 className="text-xl font-bold text-white mb-4">Common Issues & Solutions</h2>
            
            <div className="space-y-3">
              <div className="glass rounded-xl p-4">
                <h4 className="text-white font-medium mb-2 flex items-center gap-2">
                  <WarningCircle size={16} className="text-amber-400" />
                  WebhooksLogo not receiving data
                </h4>
                <ul className="text-[#A0AEC0] text-sm space-y-1 ml-6">
                  <li>• Ensure agent is deployed/active</li>
                  <li>• Check the webhook URL is correct</li>
                  <li>• Verify request uses POST method</li>
                  <li>• Check Content-Type is application/json</li>
                </ul>
              </div>

              <div className="glass rounded-xl p-4">
                <h4 className="text-white font-medium mb-2 flex items-center gap-2">
                  <WarningCircle size={16} className="text-amber-400" />
                  AI node returns error
                </h4>
                <ul className="text-[#A0AEC0] text-sm space-y-1 ml-6">
                  <li>• Check API keys in Settings → Integrations</li>
                  <li>• Verify system prompt syntax</li>
                  <li>• Test with simpler input first</li>
                </ul>
              </div>

              <div className="glass rounded-xl p-4">
                <h4 className="text-white font-medium mb-2 flex items-center gap-2">
                  <WarningCircle size={16} className="text-amber-400" />
                  Can't see data between nodes
                </h4>
                <ul className="text-[#A0AEC0] text-sm space-y-1 ml-6">
                  <li>• Use Test mode to run individual nodes</li>
                  <li>• Check Session Logs for detailed data</li>
                  <li>• Add a Log node between steps</li>
                </ul>
              </div>
            </div>
          </motion.section>

          {/* Next Steps CTA */}
          <motion.section
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.6 }}
            className="glass rounded-xl p-6 text-center"
          >
            <h2 className="text-xl font-bold text-white mb-3">Ready to Build More?</h2>
            <p className="text-[#A0AEC0] text-sm mb-4 max-w-lg mx-auto">
              Now that you understand the basics, try adding conditional logic, connecting to Slack, or building a voice agent!
            </p>
            <div className="flex flex-col sm:flex-row gap-3 justify-center">
              <Link
                to="/partner-pro/ai-workforce/agents"
                className="inline-flex items-center justify-center gap-2 px-5 py-2.5 bg-gradient-to-r from-[#6366F1] to-[#8B5CF6] text-white font-medium rounded-lg hover:opacity-90 transition-opacity text-sm"
              >
                Create Your Agent
                <ArrowRight size={16} />
              </Link>
              <Link
                to="/ai-agents"
                className="inline-flex items-center justify-center gap-2 px-5 py-2.5 glass border border-[rgba(255,255,255,0.1)] text-white font-medium rounded-lg hover:bg-[rgba(255,255,255,0.05)] transition-colors text-sm"
              >
                More Tutorials
              </Link>
            </div>
          </motion.section>
        </div>
      </div>
    </>
  );
};

export default WebhookTutorialPage;
